import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FILTER_TYPE_OPTIONS, FILTER_TYPES, DATE_PRESET_OPTIONS, DATE_PRESETS, FILTER_TYPE_LABELS } from "./constants";
import { getNodesForStages } from "./workflowUtils";

const INITIAL_FILTER = {
  type: null,
  stageIds: [],
  assignedUsers: [],
  nodeIds: [],
  datePreset: "",
  customStart: "",
  customEnd: "",
  activeStage: null,
  customFieldKey: "",
  customOperator: "eq",
  customValue: "",
};

function isDateFilterType(type) {
  return (
    type === FILTER_TYPES.CREATED_DATE ||
    type === FILTER_TYPES.UPDATED_DATE ||
    type === FILTER_TYPES.STAGE_UPDATED
  );
}

function toggleArrayValue(values, value) {
  return values.includes(value)
    ? values.filter((existingValue) => existingValue !== value)
    : [...values, value];
}

/* Single Filter popover.*/
function FilterPopover({ isOpen, onClose, stages, users, workflowStages, filters, onApply, customFields = [] }) {
  const popoverRef = useRef(null);

  const [filterBy, setFilterBy] = useState(INITIAL_FILTER);

  useEffect(() => {
    if (!isOpen) return;
    setFilterBy({
      ...INITIAL_FILTER,
      stageIds: filters.stageIds,
      nodeIds: filters.nodeIds,
      assignedUsers: filters.assignedTo,
    });
  }, [isOpen, filters]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const nodesForActiveStage = useMemo(
    () => (filterBy.activeStage ? getNodesForStages(workflowStages, [filterBy.activeStage.id]) : []),
    [workflowStages, filterBy.activeStage]
  );

  const stageById = useMemo(() => new Map(stages.map((stage) => [stage.id, stage])), [stages]);

  const setFilterField = useCallback((field, value) => {
    setFilterBy((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleFilterArrayField = useCallback((field, id, onToggle) => {
    setFilterBy((prev) => ({
      ...prev,
      [field]: toggleArrayValue(prev[field], id),
      ...(onToggle ? onToggle(prev, id) : null),
    }));
  }, []);

  const toggleStage = (id) =>
    toggleFilterArrayField("stageIds", id, (prev, stageId) => ({
      activeStage: prev.activeStage?.id === stageId ? null : prev.activeStage,
    }));

  const toggleUser = (id) => toggleFilterArrayField("assignedUsers", id);

  const toggleNode = (id) => toggleFilterArrayField("nodeIds", id);

  const handleApply = useCallback(() => {
    const { type, stageIds, nodeIds, assignedUsers, datePreset, customStart, customEnd, customFieldKey, customOperator, customValue } = filterBy;

    if (!type) return;

    if (type === FILTER_TYPES.STAGE) {
      if (!stageIds.length) return;
      onApply(FILTER_TYPES.STAGE, { stageIds, nodeIds });
    } else if (type === FILTER_TYPES.ASSIGNED_TO) {
      if (!assignedUsers.length) return;
      onApply(FILTER_TYPES.ASSIGNED_TO, assignedUsers);
    } else if (isDateFilterType(type)) {
      if (!datePreset) return;

      const isCustom = datePreset === DATE_PRESETS.CUSTOM_RANGE;
      if (isCustom && (!customStart || !customEnd)) return;

      onApply(type, {
        preset: datePreset,
        customRange: isCustom ? { startDate: customStart, endDate: customEnd } : null,
      });
    } else if (type === FILTER_TYPES.CUSTOM) {
      if (!customFieldKey || (!["empty", "not_empty"].includes(customOperator) && customValue === "")) return;
      const field = customFields.find((item) => item.key === customFieldKey);
      let value = customValue;
      if (field?.field_type === "checkbox") value = customValue === "true";
      if (field?.field_type === "multi_select") value = customValue ? [customValue] : [];
      onApply(type, { key: customFieldKey, condition: { operator: customOperator, value } });
    } else {
      return;
    }

    onClose();
  }, [customFields, filterBy, onApply, onClose]);

  if (!isOpen) return null;

  const dateFilterLabel = FILTER_TYPE_LABELS[filterBy.type] || "";

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Configure filter"
      className="absolute left-0 top-full z-20 mt-2 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
    >
      {!filterBy.type && (
        <div className="flex flex-col">
          <FieldLabel text="Choose a filter" />
          {FILTER_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilterField("type", option.value)}
              className="rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {filterBy.type === FILTER_TYPES.STAGE && filterBy.activeStage === null && (
        <div className="flex flex-col gap-3">
          <FieldLabel text="Stages" />

          {filterBy.stageIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filterBy.stageIds.map((id) => (
                <span
                  key={id}
                  className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                >
                  {stageById.get(id)?.name}
                  <button onClick={() => toggleStage(id)} className="ml-1 text-blue-500 hover:text-red-500">
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto rounded-lg border">
            {stages.map((stage) => (
              <div key={stage.id} className="flex items-center justify-between border-b px-3 py-3 hover:bg-slate-50">
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={filterBy.stageIds.includes(stage.id)}
                    onChange={() => toggleStage(stage.id)}
                  />
                  <span className="text-sm font-medium">{stage.name}</span>
                </label>

                <button
                  type="button"
                  onClick={() => setFilterField("activeStage", stage)}
                  className="rounded p-1 hover:bg-slate-200"
                >
                  →
                </button>
              </div>
            ))}
          </div>

          <PopoverActions
            onBack={() => setFilterField("type", null)}
            onApply={handleApply}
            disabled={!filterBy.stageIds.length}
          />
        </div>
      )}

      {filterBy.type === FILTER_TYPES.STAGE && filterBy.activeStage !== null && (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setFilterField("activeStage", null)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            ← Back to Stages
          </button>

          <FieldLabel text={filterBy.activeStage.name} />

          {filterBy.nodeIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {filterBy.nodeIds.map((id) => {
                const node = nodesForActiveStage.find((n) => n.id === id);
                if (!node) return null;

                return (
                  <span
                    key={id}
                    className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                  >
                    {node.name}
                    <button type="button" onClick={() => toggleNode(id)} className="text-blue-600 hover:text-red-500">
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
            {nodesForActiveStage.map((node) => (
              <label
                key={node.id}
                className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={filterBy.nodeIds.includes(node.id)}
                  onChange={() => toggleNode(node.id)}
                  className="h-4 w-4 rounded"
                />
                <span className="text-sm text-slate-700">{node.name}</span>
              </label>
            ))}
          </div>

          <PopoverActions
            onBack={() => setFilterField("activeStage", null)}
            onApply={handleApply}
            disabled={!filterBy.stageIds.length}
          />
        </div>
      )}

      {filterBy.type === FILTER_TYPES.ASSIGNED_TO && (
        <div className="flex flex-col gap-3">
          <FieldLabel text="Assigned To" />
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
            {[
              {
                user_id: "unassigned",
                displayName: "Unassigned",
                email: "",
              },
              ...users.map((user) => ({
                ...user,
                displayName: user.user?.name || "Unnamed User",
                email: user.user?.email || "",
              })),
            ].map((user) => {
              const isSelected = filterBy.assignedUsers.includes(
                user.user_id
              );

              return (
                <label
                  key={user.user_id}
                  className={`flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 hover:bg-slate-50 ${isSelected ? "bg-blue-50" : ""
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleUser(user.user_id)}
                    className="h-4 w-4 rounded"
                  />

                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-700">
                      {user.displayName}
                    </span>

                    {user.email && (
                      <span className="text-xs text-slate-400">
                        {user.email}
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          <PopoverActions
            onBack={() => setFilterField("type", null)}
            onApply={handleApply}
            disabled={!filterBy.assignedUsers.length}
          />
        </div>
      )}

      {isDateFilterType(filterBy.type) && (
        <div className="flex flex-col gap-3">
          <FieldLabel text={dateFilterLabel} />
          <select
            value={filterBy.datePreset}
            onChange={(event) => setFilterField("datePreset", event.target.value)}
            className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
          >
            <option value="">Select a range</option>
            {DATE_PRESET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {filterBy.datePreset === DATE_PRESETS.CUSTOM_RANGE && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filterBy.customStart}
                onChange={(event) => setFilterField("customStart", event.target.value)}
                className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={filterBy.customEnd}
                onChange={(event) => setFilterField("customEnd", event.target.value)}
                className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
              />
            </div>
          )}

          <PopoverActions
            onBack={() => setFilterField("type", null)}
            onApply={handleApply}
            disabled={
              !filterBy.datePreset ||
              (filterBy.datePreset === DATE_PRESETS.CUSTOM_RANGE && (!filterBy.customStart || !filterBy.customEnd))
            }
          />
        </div>
      )}

      {filterBy.type === FILTER_TYPES.CUSTOM && (
        <div className="flex flex-col gap-3">
          <FieldLabel text="Lead field" />
          <select value={filterBy.customFieldKey} onChange={(event) => setFilterBy((current) => ({ ...current, customFieldKey: event.target.value, customOperator: "eq", customValue: "" }))} className="rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-700">
            <option value="">Select a field</option>
            {customFields.filter((field) => field.is_filterable).map((field) => <option key={field.key} value={field.key}>{field.label}</option>)}
          </select>
          {filterBy.customFieldKey && <>
            <select value={filterBy.customOperator} onChange={(event) => setFilterField("customOperator", event.target.value)} className="rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-700">
              <option value="eq">Equals</option><option value="contains">Contains</option>{!filterBy.customFieldKey.startsWith("starter:") && <><option value="gt">Greater than</option><option value="gte">At least</option><option value="lt">Less than</option><option value="lte">At most</option></>}<option value="empty">Is empty</option><option value="not_empty">Is not empty</option>
            </select>
            {!["empty", "not_empty"].includes(filterBy.customOperator) && (() => {
              const field = customFields.find((item) => item.key === filterBy.customFieldKey);
              if (["single_select", "multi_select"].includes(field?.field_type)) return <select value={filterBy.customValue} onChange={(event) => setFilterField("customValue", event.target.value)} className="rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-700"><option value="">Select a value</option>{(field.options || []).map((option) => { const value = typeof option === "object" ? option.value : option; const label = typeof option === "object" ? option.label : option; return <option key={value} value={value}>{label}</option>; })}</select>;
              if (field?.field_type === "checkbox") return <select value={filterBy.customValue} onChange={(event) => setFilterField("customValue", event.target.value)} className="rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-700"><option value="">Select</option><option value="true">Yes</option><option value="false">No</option></select>;
              return <input type={["number", "currency"].includes(field?.field_type) ? "number" : field?.field_type === "date" ? "date" : "text"} value={filterBy.customValue} onChange={(event) => setFilterField("customValue", event.target.value)} placeholder="Filter value" className="rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-700" />;
            })()}
          </>}
          <PopoverActions onBack={() => setFilterField("type", null)} onApply={handleApply} disabled={!filterBy.customFieldKey || (!["empty", "not_empty"].includes(filterBy.customOperator) && filterBy.customValue === "")} />
        </div>
      )}
    </div>
  );
}

function FieldLabel({ text }) {
  return <span className="mb-1 block px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{text}</span>;
}

function PopoverActions({ onBack, onApply, disabled }) {
  return (
    <div className="mt-1 flex items-center justify-between">
      <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">
        Back
      </button>
      <button
        type="button"
        onClick={onApply}
        disabled={disabled}
        className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Apply
      </button>
    </div>
  );
}

export default React.memo(FilterPopover);
